import { useCallback, useRef, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Upload, FileText, Loader2, AlertCircle, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function UploadPanel({
  state, // "idle" | "parsing" | "error"
  errorCode,
  onFile,
  onDemo,
  onRetry,
  onCancel,
  compact = false,
  tone = "light",
}) {
  const { t } = useLang();
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const openPicker = () => inputRef.current?.click();

  const handleFiles = useCallback(
    (files) => {
      if (!files || !files.length) return;
      onFile(files[0]);
    },
    [onFile]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer?.files);
    },
    [handleFiles]
  );

  const errorMessage = (() => {
    if (!errorCode) return null;
    if (errorCode === "invalid_type") return t.upload.invalidType;
    if (errorCode === "too_large") return t.upload.tooLarge;
    if (errorCode === "timeout") return t.upload.timeout;
    if (errorCode === "unsupported_lab") return t.upload.unsupportedLab;
    if (errorCode === "network") return `${t.upload.networkError} ${t.upload.corsHint}`;
    return t.upload.networkError;
  })();

  const isHero = tone === "hero";

  return (
    <div
      data-testid="upload-panel"
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={`relative w-full overflow-hidden rounded-[28px] border backdrop-blur-xl transition-all ${
        compact ? "p-5" : "p-7 md:p-9"
      } ${
        isHero
          ? isDragging
            ? "border-white/60 bg-white/20"
            : "border-white/30 bg-white/12"
          : isDragging
          ? "border-[color:var(--sentinel)] bg-[color:var(--sentinel-tint)]/60"
          : "border-zinc-200/80 bg-white/60"
      }`}
      style={{
        boxShadow: isHero
          ? "0 24px 60px -24px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.15) inset"
          : "0 1px 0 rgba(255,255,255,0.7) inset, 0 24px 60px -32px rgba(81,75,186,0.25)",
      }}
    >
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-3 rounded-[22px] dashed-ring ${isHero ? "opacity-40" : "opacity-70"}`}
      />

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="upload-file-input"
      />

      <AnimatePresence mode="wait">
        {state === "parsing" && (
          <motion.div
            key="parsing"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="relative z-10 flex flex-col items-center gap-4 py-6 text-center"
            data-testid="upload-state-parsing"
          >
            <Loader2 className="h-10 w-10 animate-spin text-[color:var(--sentinel)]" />
            <p className={`text-base font-semibold ${isHero ? "text-white" : "text-zinc-900"}`}>
              {t.upload.parsing}
            </p>
            <p className={`text-sm ${isHero ? "text-white/70" : "text-zinc-500"}`}>{t.analyzing.note}</p>
            {onCancel && (
              <button
                onClick={onCancel}
                className="text-xs font-semibold text-zinc-500 underline hover:text-zinc-900"
                data-testid="upload-cancel"
              >
                {t.upload.cancel}
              </button>
            )}
          </motion.div>
        )}

        {state === "error" && (
          <motion.div
            key="error"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="relative z-10 flex flex-col items-center gap-4 py-4 text-center"
            data-testid="upload-state-error"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <p className="max-w-md text-sm font-medium text-zinc-800">{errorMessage}</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={onRetry}
                className="btn-ghost"
                data-testid="upload-retry"
              >
                {t.upload.retry}
              </button>
              <button onClick={onDemo} className="btn-primary" data-testid="upload-try-demo">
                <Sparkles className="h-4 w-4" />
                {t.upload.tryDemo}
              </button>
            </div>
          </motion.div>
        )}

        {state === "idle" && (
          <motion.div
            key="idle"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="relative z-10 flex flex-col items-center gap-4 text-center"
            data-testid="upload-state-idle"
          >
            <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${isHero ? "bg-white/15" : "bg-[color:var(--sentinel-tint)]"}`}>
              <Upload className={`h-6 w-6 ${isHero ? "text-white" : "text-[color:var(--sentinel)]"}`} strokeWidth={2.2} />
            </div>
            <div>
              <h3 className={`text-xl font-semibold md:text-2xl ${isHero ? "text-white" : "text-zinc-900"}`}>
                {isDragging ? t.upload.drop : t.upload.title}
              </h3>
              <p className={`mt-1 text-sm ${isHero ? "text-white/75" : "text-zinc-500"}`}>{t.upload.subtitle}</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
              <button
                onClick={openPicker}
                className={isHero ? "inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[color:var(--sentinel)] transition hover:bg-[color:var(--sentinel-tint)]" : "btn-primary"}
                data-testid="upload-choose-file"
              >
                <FileText className="h-4 w-4" />
                {t.upload.choose}
              </button>
              <button
                onClick={onDemo}
                className={isHero ? "inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10" : "btn-ghost"}
                data-testid="upload-demo-cta"
              >
                <Sparkles className="h-4 w-4" />
                {t.upload.tryDemo}
              </button>
            </div>
            <p className={`pt-1 text-[11px] uppercase tracking-[0.18em] ${isHero ? "text-white/50" : "text-zinc-400"}`}>
              {t.upload.accepted}
            </p>
            <p className={`max-w-md text-[12px] leading-relaxed ${isHero ? "text-white/55" : "text-zinc-400"}`}>
              {t.upload.privacy}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
